import Immutable from 'immutable';
import * as Models from 'models';

export type ElementForRender = {
  layouts: Models.CombinedLayoutsList;
  isCarousel: boolean;
  carouselId?: string;
};

const addToExistingCarouselGroup = (
  elementsForRender: ElementForRender[],
  layout: Models.CombinedLayoutMap,
  carouselId: string,
): boolean => {
  const existingCarouselGroup = elementsForRender.find(
    group => group.isCarousel && group.carouselId === carouselId,
  );

  if (existingCarouselGroup) {
    existingCarouselGroup.layouts = existingCarouselGroup.layouts.push(layout);

    return true;
  }

  return false;
};

const createNewCarouselGroup = (layout: Models.CombinedLayoutMap, carouselId: string): ElementForRender => ({
  layouts: Immutable.List([layout]),
  isCarousel: true,
  carouselId,
});

const finishCurrentGroupIfNeeded = (
  currentGroup: ElementForRender | null,
  elementsForRender: ElementForRender[],
): ElementForRender | null => {
  if (currentGroup && !currentGroup.isCarousel) {
    elementsForRender.push(currentGroup);

    return null;
  }

  return currentGroup;
};

const processCarouselItem = (
  layout: Models.CombinedLayoutMap,
  elementsForRender: ElementForRender[],
  currentGroup: ElementForRender | null,
): ElementForRender | null => {
  const updatedCurrentGroup = finishCurrentGroupIfNeeded(currentGroup, elementsForRender);

  const layoutWithCarouselItem = layout as unknown as Models.LayeredLayoutMap;
  const carouselItem = layoutWithCarouselItem.get('carouselItem');
  const carouselId = carouselItem?.get('carouselId');

  if (carouselId) {
    const wasAddedToExisting = addToExistingCarouselGroup(elementsForRender, layout, carouselId);
    if (!wasAddedToExisting) {
      elementsForRender.push(createNewCarouselGroup(layout, carouselId));
    }
  }

  return updatedCurrentGroup;
};

const processRegularItem = (
  layout: Models.CombinedLayoutMap,
  currentGroup: ElementForRender | null,
): ElementForRender => {
  if (currentGroup && !currentGroup.isCarousel) {
    return {
      ...currentGroup,
      layouts: currentGroup.layouts.push(layout),
    };
  }

  return {
    layouts: Immutable.List([layout]),
    isCarousel: false,
  };
};

export const createElementsForRender = (layouts: Models.CombinedLayoutsList): ElementForRender[] => {
  const elementsForRender: ElementForRender[] = [];
  let currentGroup: ElementForRender | null = null;

  layouts.forEach((layout) => {
    const isCarouselItem = (layout as Models.LayoutMap).get('carouselItem');

    if (isCarouselItem) {
      currentGroup = processCarouselItem(layout, elementsForRender, currentGroup);
    } else {
      currentGroup = processRegularItem(layout, currentGroup);
    }
  });

  if (currentGroup) {
    elementsForRender.push(currentGroup);
  }

  return elementsForRender;
};
